import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MessagingService from '../components/MessagingService';
import { fetchUsers } from '../components/UserService';
import { fetchProducts as fetchProductsStream } from '../components/ProductService';
import { fetchCategories } from '../components/ProductCategoryService';
import newMessageSound from '../sounds/messaging-new-message.mp3';

const CHANNEL_TYPES = [
  { value: 'all', label: 'Tous les canaux' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'site', label: 'Site web' },
  { value: 'supplier', label: 'Fournisseurs' },
  { value: 'b2b', label: 'B2B' },
  { value: 'other', label: 'Autre' }
];

const CHANNEL_STYLE = {
  all: { icon: 'fa-solid fa-layer-group', color: '#111827', bg: '#f3f4f6' },
  whatsapp: { icon: 'fa-brands fa-whatsapp', color: '#16a34a', bg: '#dcfce7' },
  instagram: { icon: 'fa-brands fa-instagram', color: '#db2777', bg: '#fce7f3' },
  site: { icon: 'fa-solid fa-globe', color: '#2563eb', bg: '#dbeafe' },
  supplier: { icon: 'fa-solid fa-truck-field', color: '#d97706', bg: '#fef3c7' },
  b2b: { icon: 'fa-solid fa-handshake', color: '#0f766e', bg: '#ccfbf1' },
  other: { icon: 'fa-solid fa-comment-dots', color: '#64748b', bg: '#f1f5f9' }
};

const emptyChannel = {
  name: '',
  type: 'whatsapp',
  provider: 'meta',
  enabled: true,
  external_account_id: '',
  external_sender_id: '',
  webhook: { verify_token: '' },
  credentials: {
    access_token: '',
    phone_number_id: '',
    instagram_business_id: ''
  }
};

const channelLabel = (type) => CHANNEL_TYPES.find((item) => item.value === type)?.label || type;

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return String(value.$oid || value._id || value.$id || value.id || value.value || '');
  return String(value);
};

const resolveProductImageUrl = (product) => {
  const source = product?.url_product || product?.imageUrl || product?.image || (Array.isArray(product?.gallery_images) ? product.gallery_images[0] : '');
  if (!source) return '';
  const normalized = String(source);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  return `${process.env.REACT_APP_DATA_PIC_PATH || process.env.REACT_APP_PEGASUS_API_URL || ''}/${normalized.replace(/^\//, '')}`;
};

const resolveCategoryImageUrl = (category) => {
  const source = category?.image || category?.src || '';
  if (!source) return '';
  const normalized = String(source);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  return `${process.env.REACT_APP_PEGASUS_API_URL || ''}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
};

const getProductCategoryIds = (product) => {
  const ids = new Set();
  [product?.category, product?.category_id, product?.categoryId].forEach((entry) => {
    const id = normalizeId(entry);
    if (id) ids.add(id);
  });
  if (Array.isArray(product?.categories)) {
    product.categories.forEach((entry) => {
      const id = normalizeId(entry?._id || entry?.id || entry?.value || entry);
      if (id) ids.add(id);
    });
  }
  return ids;
};

const getProductPrice = (product) => (
  product?.discounted_price ||
  product?.regular_price ||
  product?.price ||
  product?.price_ttc ||
  product?.priceTTC ||
  ''
);

const formatSiteMoney = (value, currency = 'DHS') => {
  const displayCurrency = String(currency || 'DHS').replace(/^MAD$/i, 'DHS') || 'DHS';
  if (Number.isFinite(Number(value))) {
    return `${Number(value).toFixed(2)} ${displayCurrency}`;
  }
  const cleaned = String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\bMAD\b/gi, 'DHS');
  if (!cleaned) return '';
  return /\b(DHS|DH|EUR|USD)\b/i.test(cleaned) ? cleaned : `${cleaned} ${displayCurrency}`;
};

const getProductReference = (product) => product?.ref || product?.reference || product?.barcode || product?.sku || '';

const ChannelIcon = ({ type, active = false, size = 36 }) => {
  const style = CHANNEL_STYLE[type] || CHANNEL_STYLE.other;
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-circle"
      style={{
        width: size,
        height: size,
        background: active ? 'rgba(255,255,255,.18)' : style.bg,
        color: active ? '#fff' : style.color,
        flex: '0 0 auto'
      }}
    >
      <i className={style.icon} />
    </span>
  );
};

const IconCircleButton = ({ icon, title, variant = 'secondary', count = null, active = false, onClick }) => (
  <button
    type="button"
    className={`btn btn-sm btn-outline-${variant} rounded-circle d-inline-flex align-items-center justify-content-center position-relative ${active ? `btn-${variant} text-white` : ''}`}
    title={title}
    aria-label={title}
    onClick={onClick}
    style={{ width: 34, height: 34, flex: '0 0 34px' }}
  >
    <i className={icon} />
    {Number(count || 0) > 0 && (
      <span className={`position-absolute top-0 start-100 translate-middle badge rounded-pill bg-${variant}`} style={{ fontSize: 9, minWidth: 18 }}>
        {Number(count) > 99 ? '99+' : count}
      </span>
    )}
  </button>
);

const getConversationName = (conversation) => {
  const contact = conversation?.contact || {};
  return contact.name || contact.username || contact.phone || contact.external_id || 'Client inconnu';
};

const getSiteCustomerContext = (conversation) => conversation?.metadata?.customer_context || conversation?.metadata?.customerContext || {};

const getSiteCartCount = (conversation) => {
  const cart = getSiteCustomerContext(conversation)?.cart || {};
  if (Number.isFinite(Number(cart.count))) return Number(cart.count);
  return Array.isArray(cart.items) ? cart.items.reduce((sum, item) => sum + Number(item?.quantity || 0), 0) : 0;
};

const getSiteOrdersCount = (conversation) => {
  const orders = getSiteCustomerContext(conversation)?.orders || {};
  if (Number.isFinite(Number(orders.count))) return Number(orders.count);
  return Array.isArray(orders.items) ? orders.items.length : 0;
};

const getSitePresence = (conversation) => conversation?.metadata?.presence || {};

const getSitePresenceSessions = (conversation) => {
  const presence = getSitePresence(conversation);
  return Array.isArray(presence.sessions) && presence.sessions.length > 0 ? presence.sessions : (presence.last_seen_at ? [presence] : []);
};

const isSiteConversationOnline = (conversation) => {
  return getSitePresenceSessions(conversation).some((session) => {
    const lastSeen = new Date(session.last_seen_at).getTime();
    return Number.isFinite(lastSeen) && Date.now() - lastSeen < 90000;
  });
};

const getWebhookUrl = (type) => {
  const baseUrl = process.env.REACT_APP_PEGASUS_API_URL || '';
  return `${baseUrl}/messaging/webhooks/${type}`;
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  } catch (error) {
    return '';
  }
};

const getCollectionSignature = (items, pick) => JSON.stringify((Array.isArray(items) ? items : []).map(pick));

const areCollectionsEqual = (current, next, pick) => getCollectionSignature(current, pick) === getCollectionSignature(next, pick);

const channelSignature = (channel) => ({
  id: channel?._id,
  name: channel?.name,
  type: channel?.type,
  enabled: channel?.enabled,
  updatedAt: channel?.updatedAt
});

const conversationSignature = (conversation) => ({
  id: conversation?._id,
  type: conversation?.channel_type,
  unread: conversation?.unread_count || 0,
  preview: conversation?.last_message_preview || '',
  last: conversation?.last_message_at || '',
  contact: conversation?.contact || {},
  metadata: conversation?.metadata || {}
});

const messageSignature = (message) => ({
  id: message?._id,
  type: message?.message_type || '',
  text: message?.text || '',
  status: message?.status || '',
  error: message?.metadata?.error || '',
  mediaUrl: message?.media?.url || '',
  mediaMime: message?.media?.mime_type || '',
  mediaExternal: message?.media?.external_media_id || '',
  product: message?.metadata?.product || {},
  createdAt: message?.createdAt || '',
  direction: message?.direction || ''
});

const getMessageStatusLabel = (message) => {
  if (message?.status === 'blocked_window' || message?.metadata?.reason === 'instagram_allowed_window_expired') {
    return 'Fenetre Instagram expiree';
  }
  return message?.status || '';
};

const getMessageErrorHint = (message) => {
  if (message?.status === 'blocked_window' || message?.metadata?.reason === 'instagram_allowed_window_expired') {
    return 'Le client doit renvoyer un message sur Instagram avant qu on puisse repondre via l API.';
  }
  return message?.metadata?.error || '';
};

const getMediaTypeLabel = (message) => {
  const type = String(message?.message_type || '').toLowerCase();
  const mimeType = String(message?.media?.mime_type || '').toLowerCase();
  if (type === 'audio' || mimeType.startsWith('audio/')) return 'Audio';
  if (type === 'video' || mimeType.startsWith('video/')) return 'Video';
  if (type === 'image' || mimeType.startsWith('image/')) return 'Image';
  if (type === 'document' || type === 'file' || mimeType) return 'Document';
  return type || 'Message';
};

const getMediaIcon = (message) => {
  const label = getMediaTypeLabel(message).toLowerCase();
  if (label === 'audio') return 'fa-file-audio';
  if (label === 'video') return 'fa-file-video';
  if (label === 'image') return 'fa-file-image';
  if (label === 'document') return 'fa-file-lines';
  return 'fa-paperclip';
};

const isRetryableMessage = (message) => (
  message?.direction === 'outbound' &&
  ['failed', 'blocked_window', 'configuration_required'].includes(message?.status)
);

const INSTAGRAM_REPLY_WINDOW_MS = 24 * 60 * 60 * 1000;
const NOTIFICATION_REPEAT_COUNT = 3;

const playNotificationSound = async () => {
  for (let i = 0; i < NOTIFICATION_REPEAT_COUNT; i += 1) {
    const audio = new Audio(newMessageSound);
    audio.volume = 0.85;
    try {
      await audio.play();
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
      });
    } catch (error) {
      break;
    }
  }
};

function MessagingInbox({ embedded = false } = {}) {
  const roles = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('roles') || '[]');
    } catch (error) {
      return [];
    }
  }, []);
  const isAdmin = roles.includes('admin');
  const [channels, setChannels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [supportUsers, setSupportUsers] = useState([]);
  const [activeType, setActiveType] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [search, setSearch] = useState('');
  const [text, setText] = useState('');
  const [channelForm, setChannelForm] = useState(emptyChannel);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState('');
  const [sendingImages, setSendingImages] = useState(false);
  const [sendingProducts, setSendingProducts] = useState(false);
  const [showCustomerContext, setShowCustomerContext] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [notice, setNotice] = useState('');
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productPriceMin, setProductPriceMin] = useState('');
  const [productPriceMax, setProductPriceMax] = useState('');
  const [selectedProductCategory, setSelectedProductCategory] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productPriceOverrides, setProductPriceOverrides] = useState({});
  const [visibleProductLimit, setVisibleProductLimit] = useState(60);
  const messagesRequestRef = useRef(0);
  const syncInProgressRef = useRef(false);
  const selectedConversationRef = useRef(null);
  const unreadTotalRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const filteredChannelTypes = useMemo(() => {
    const counts = channels.reduce((acc, channel) => {
      acc[channel.type] = (acc[channel.type] || 0) + 1;
      return acc;
    }, {});
    return CHANNEL_TYPES.map((type) => ({ ...type, count: type.value === 'all' ? channels.length : (counts[type.value] || 0) }));
  }, [channels]);

  const lastInboundMessageAt = useMemo(() => {
    const inboundMessages = messages
      .filter((message) => message.direction === 'inbound')
      .map((message) => new Date(message.provider_timestamp || message.createdAt).getTime())
      .filter((timestamp) => Number.isFinite(timestamp));
    return inboundMessages.length ? Math.max(...inboundMessages) : null;
  }, [messages]);

  const isInstagramReplyWindowExpired = Boolean(
    selectedConversation?.channel_type === 'instagram' &&
    (!lastInboundMessageAt || Date.now() - lastInboundMessageAt > INSTAGRAM_REPLY_WINDOW_MS)
  );

  const messageInputDisabled = !selectedConversation || messagesLoading || isInstagramReplyWindowExpired;

  const pegasusCategories = useMemo(() => (
    Array.isArray(productCategories)
      ? productCategories.filter((category) => String(category?.type || '').toLowerCase() === 'pegasus')
      : []
  ), [productCategories]);

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    const minPrice = productPriceMin === '' ? null : Number(productPriceMin);
    const maxPrice = productPriceMax === '' ? null : Number(productPriceMax);
    return (Array.isArray(products) ? products : [])
      .filter((product) => {
        if (!selectedProductCategory) return true;
        return getProductCategoryIds(product).has(selectedProductCategory);
      })
      .filter((product) => {
        const price = Number(String(getProductPrice(product) || '').replace(',', '.').replace(/[^\d.-]/g, ''));
        if (!Number.isFinite(price)) return minPrice === null && maxPrice === null;
        if (minPrice !== null && price < minPrice) return false;
        if (maxPrice !== null && price > maxPrice) return false;
        return true;
      })
      .filter((product) => {
        if (!query) return true;
        return [
          product?.name,
          product?.description,
          getProductReference(product),
          product?.barcode
        ].some((value) => String(value || '').toLowerCase().includes(query));
      })
      .sort((a, b) => new Date(b?.createdAt || b?.updatedAt || 0).getTime() - new Date(a?.createdAt || a?.updatedAt || 0).getTime());
  }, [products, productPriceMax, productPriceMin, productSearch, selectedProductCategory]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, visibleProductLimit),
    [filteredProducts, visibleProductLimit]
  );

  const selectedProductsForSend = useMemo(() => {
    const selected = new Set(selectedProductIds);
    return (Array.isArray(products) ? products : []).filter((product) => selected.has(String(product._id || product.id)));
  }, [products, selectedProductIds]);

  const getEffectiveProductPrice = useCallback((product) => {
    const productId = String(product?._id || product?.id || '');
    return productPriceOverrides[productId] || getProductPrice(product);
  }, [productPriceOverrides]);

  const scrollMessagesToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const scroll = () => {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
      container.scrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    };

    requestAnimationFrame(() => {
      scroll();
      window.setTimeout(scroll, 100);
      window.setTimeout(scroll, 350);
    });
  }, []);

  const loadChannels = useCallback(async () => {
    const data = await MessagingService.fetchChannels();
    const nextChannels = Array.isArray(data) ? data : [];
    setChannels((prev) => (areCollectionsEqual(prev, nextChannels, channelSignature) ? prev : nextChannels));
  }, []);

  const loadProductsCatalog = useCallback(async () => {
    setProductsLoading(true);
    try {
      const [nextProducts, categoriesPayload] = await Promise.all([
        fetchProductsStream(),
        fetchCategories()
      ]);
      setProducts(Array.isArray(nextProducts) ? nextProducts : []);
      const nextCategories = Array.isArray(categoriesPayload)
        ? categoriesPayload
        : (Array.isArray(categoriesPayload?.data) ? categoriesPayload.data : []);
      setProductCategories(nextCategories);
    } catch (error) {
      setNotice(error.response?.data?.error || error.message || 'Chargement produits impossible.');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async (options = {}) => {
    if (!options.silent) setLoading(true);
    try {
      const params = {};
      if (activeType !== 'all') params.channelType = activeType;
      if (search.trim()) params.search = search.trim();
      const data = await MessagingService.fetchConversations(params);
      const nextConversations = Array.isArray(data) ? data : [];
      const nextUnreadTotal = nextConversations.reduce((sum, conversation) => sum + Number(conversation.unread_count || 0), 0);
      if (options.notify && unreadTotalRef.current !== null && nextUnreadTotal > unreadTotalRef.current) {
        playNotificationSound();
      }
      unreadTotalRef.current = nextUnreadTotal;
      setConversations((prev) => (areCollectionsEqual(prev, nextConversations, conversationSignature) ? prev : nextConversations));
      setSelectedConversation((prev) => {
        if (!prev?._id) return prev;
        const updated = nextConversations.find((conversation) => conversation._id === prev._id);
        return updated ? { ...prev, ...updated } : prev;
      });
      return nextConversations;
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, [activeType, search]);

  const loadMessages = useCallback(async (conversationId, options = {}) => {
    if (!conversationId) return;
    const requestId = messagesRequestRef.current + 1;
    messagesRequestRef.current = requestId;
    if (!options.silent) setMessagesLoading(true);
    try {
      const data = await MessagingService.fetchMessages(conversationId);
      if (messagesRequestRef.current === requestId) {
        if (data?.conversation) {
          setSelectedConversation((prev) => (prev?._id === data.conversation._id ? { ...prev, ...data.conversation } : prev));
        }
        const nextMessages = Array.isArray(data) ? data : (Array.isArray(data?.messages) ? data.messages : []);
        setMessages((prev) => (areCollectionsEqual(prev, nextMessages, messageSignature) ? prev : nextMessages));
        window.dispatchEvent(new Event('pegasus:messaging-unread-refresh'));
      }
    } finally {
      if (!options.silent && messagesRequestRef.current === requestId) setMessagesLoading(false);
    }
  }, []);

  const syncMessages = useCallback(async (options = {}) => {
    if (syncInProgressRef.current) return null;
    syncInProgressRef.current = true;
    if (!options.silent) setSyncing(true);
    try {
      const result = await MessagingService.syncNow(activeType);
      const providerSync = Array.isArray(result?.provider_sync) ? result.provider_sync : [];
      const hasProviderChanges = providerSync.some((item) => Number(item?.messages_created || 0) > 0);
      if (!options.silent || hasProviderChanges) {
        setLastSyncAt(result?.checked_at || new Date().toISOString());
      }
      await loadChannels();
      const selectedBefore = selectedConversationRef.current;
      const nextConversations = await loadConversations({ silent: options.silent, notify: true });
      const selectedConversationId = selectedBefore?._id;
      const updatedSelected = Array.isArray(nextConversations)
        ? nextConversations.find((conversation) => conversation._id === selectedConversationId)
        : null;
      const selectedActivityChanged = Boolean(
        selectedBefore &&
        updatedSelected &&
        String(updatedSelected.last_message_at || '') !== String(selectedBefore.last_message_at || '')
      );
      if (selectedConversationId && (!options.silent || hasProviderChanges || selectedActivityChanged)) {
        await loadMessages(selectedConversationId, { silent: options.silent });
      }
      window.dispatchEvent(new Event('pegasus:messaging-unread-refresh'));
      return result;
    } catch (error) {
      if (!options.silent) setNotice(error.message);
      return null;
    } finally {
      syncInProgressRef.current = false;
      if (!options.silent) setSyncing(false);
    }
  }, [activeType, loadChannels, loadConversations, loadMessages]);

  const runRefresh = async () => {
    setNotice('');
    const result = await syncMessages();
    if (!result) return;

    const providerSync = Array.isArray(result.provider_sync) ? result.provider_sync : [];
    const importedMessages = providerSync.reduce((sum, item) => sum + Number(item.messages_created || 0), 0);
    const syncedConversations = providerSync.reduce((sum, item) => sum + Number(item.conversations || 0), 0);
    const failed = providerSync.find((item) => item && item.synced === false);

    if (failed) {
      setNotice(failed.error || 'Refresh termine avec une erreur provider.');
      return;
    }

    setNotice(
      providerSync.length
        ? `Refresh termine: ${syncedConversations} conversation(s), ${importedMessages} nouveau(x) message(s).`
        : 'Refresh termine.'
    );
  };

  useEffect(() => {
    if (!embedded) {
      window.dispatchEvent(new Event('pegasus:collapse-sidebar'));
    }
  }, [embedded]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchUsers()
      .then((data) => {
        const users = Array.isArray(data) ? data : [];
        setSupportUsers(users.filter((user) => Array.isArray(user.access_permissions) && user.access_permissions.includes('support')));
      })
      .catch(() => setSupportUsers([]));
  }, [isAdmin]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    loadChannels().catch((error) => setNotice(error.message));
  }, [loadChannels]);

  useEffect(() => {
    loadProductsCatalog().catch((error) => setNotice(error.message));
  }, [loadProductsCatalog]);

  useEffect(() => {
    syncMessages({ silent: false }).catch((error) => setNotice(error.message));
  }, []);

  useEffect(() => {
    loadConversations().catch((error) => setNotice(error.message));
  }, [activeType, loadConversations]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = window.setInterval(() => {
      syncMessages({ silent: true }).catch(() => {});
    }, 15000);
    return () => window.clearInterval(interval);
  }, [autoRefresh, syncMessages]);

  useEffect(() => {
    if (!selectedConversation || messagesLoading) return;
    scrollMessagesToBottom();
  }, [selectedConversation?._id, messages.length, messagesLoading, scrollMessagesToBottom]);

  useEffect(() => {
    setVisibleProductLimit(60);
  }, [productSearch, productPriceMin, productPriceMax, selectedProductCategory, showProductsModal]);

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    setShowCustomerContext(false);
    await loadMessages(conversation._id);
  };

  const saveChannel = async (event) => {
    event.preventDefault();
    const saved = await MessagingService.saveChannel(channelForm);
    setNotice('Canal sauvegarde.');
    setChannelForm(emptyChannel);
    setShowConfig(false);
    await loadChannels();
    if (!channels.some((channel) => channel._id === saved?._id)) {
      await loadConversations();
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!selectedConversation || !text.trim()) return;
    const sent = await MessagingService.sendMessage(selectedConversation._id, text.trim());
    setMessages((prev) => [...prev, sent]);
    setText('');
    await loadConversations();
  };

  const sendImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selectedConversation || messageInputDisabled || !files.length) return;
    setSendingImages(true);
    try {
      const sentImages = await MessagingService.sendImages(selectedConversation._id, files);
      setMessages((prev) => [...prev, ...(Array.isArray(sentImages) ? sentImages : [])]);
      await loadConversations({ silent: true });
    } catch (error) {
      setNotice(error.response?.data?.error || error.message || 'Envoi image impossible.');
    } finally {
      setSendingImages(false);
    }
  };

  const toggleProductSelection = (productId) => {
    const id = String(productId || '');
    if (!id) return;
    setSelectedProductIds((prev) => {
      if (prev.includes(id)) {
        setProductPriceOverrides((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        });
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const editSelectedProductPrice = (event, product) => {
    event.stopPropagation();
    const productId = String(product?._id || product?.id || '');
    if (!productId || !selectedProductIds.includes(productId)) return;

    const currentPrice = String(getEffectiveProductPrice(product) || '').replace(/\s*MAD$/i, '').trim();
    const nextPrice = window.prompt('Nouveau prix a envoyer', currentPrice);
    if (nextPrice === null) return;
    const normalizedPrice = nextPrice.trim().replace(',', '.');
    if (!normalizedPrice) return;
    setProductPriceOverrides((prev) => ({ ...prev, [productId]: normalizedPrice }));
  };

  const sendSelectedProducts = async () => {
    if (!selectedConversation || messageInputDisabled || !selectedProductsForSend.length) return;
    setSendingProducts(true);
    try {
      const payload = selectedProductsForSend.map((product) => ({
        _id: product._id || product.id || '',
        name: product.name || '',
        ref: getProductReference(product),
        price: getEffectiveProductPrice(product),
        woocommerce_id: product.woocommerce_id || product.woo_id || product.wooId || '',
        image_url: resolveProductImageUrl(product)
      }));
      const sent = await MessagingService.sendProducts(selectedConversation._id, payload);
      setMessages((prev) => [...prev, ...(Array.isArray(sent) ? sent : [])]);
      setShowProductsModal(false);
      setSelectedProductIds([]);
      setProductPriceOverrides({});
      await loadConversations({ silent: true });
    } catch (error) {
      setNotice(error.response?.data?.error || error.message || 'Envoi produits impossible.');
    } finally {
      setSendingProducts(false);
    }
  };

  const assignConversation = async (assignedTo) => {
    if (!selectedConversation?._id) return;
    const updated = await MessagingService.assignConversation(selectedConversation._id, assignedTo);
    setSelectedConversation(updated);
    setConversations((prev) => prev.map((conversation) => (conversation._id === updated._id ? { ...conversation, ...updated } : conversation)));
  };

  const retryMessage = async (message) => {
    if (!message?._id || retryingMessageId) return;
    setRetryingMessageId(message._id);
    try {
      const updated = await MessagingService.retryMessage(message._id);
      setMessages((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
      await loadConversations({ silent: true });
    } catch (error) {
      setNotice(error.response?.data?.error || error.message || 'Retry impossible.');
    } finally {
      setRetryingMessageId('');
    }
  };

  const createTestMessage = async () => {
    const channel = channels.find((item) => activeType === 'all' || item.type === activeType) || channels[0];
    if (!channel) {
      setNotice('Creez un canal avant de tester.');
      return;
    }
    await MessagingService.createTestIncoming(channel._id, {
      text: 'Message entrant de test',
      from: `test-${Date.now()}`,
      contact: { external_id: `test-${Date.now()}`, name: 'Client test' }
    });
    await loadConversations();
  };

  const editChannel = (channel) => {
    setChannelForm({
      ...emptyChannel,
      ...channel,
      webhook: { verify_token: channel?.webhook?.verify_token || '' },
      credentials: {
        ...emptyChannel.credentials,
        ...(channel.credentials || {})
      }
    });
    setShowConfig(true);
  };

  const embeddedStyles = embedded ? (
    <style>
      {`
        .messaging-page-embedded {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
          width: 100%;
        }

        .messaging-page-embedded .messaging-inbox-shell {
          flex: 1 1 auto;
          min-height: 0;
        }

        .messaging-page-embedded .messaging-chat-panel,
        .messaging-page-embedded .messaging-conversation-list {
          min-height: 0;
        }

        .messaging-page-embedded .messaging-composer {
          position: sticky;
          bottom: 0;
          z-index: 2;
        }

        @media (max-width: 991.98px) {
          .pegasus-global-messaging-modal {
            padding: 6px !important;
          }

          .pegasus-global-messaging-dialog {
            max-width: calc(100vw - 12px) !important;
            width: calc(100vw - 12px) !important;
            height: calc(100dvh - 12px) !important;
          }

          .messaging-page-embedded .messaging-inbox-shell {
            gap: 8px !important;
          }

          .messaging-page-embedded .messaging-channel-rail {
            width: 58px !important;
            flex-basis: 58px !important;
          }

          .messaging-page-embedded .messaging-conversation-list {
            width: 250px !important;
            flex-basis: 250px !important;
          }
        }

        @media (max-width: 767.98px) {
          .pegasus-global-messaging-modal .modal-header {
            padding: 8px 10px;
          }

          .pegasus-global-messaging-modal .modal-body {
            padding: 8px !important;
          }

          .messaging-page-embedded .messaging-embedded-toolbar {
            gap: 6px !important;
            margin-bottom: 8px !important;
          }

          .messaging-page-embedded .messaging-embedded-toolbar .btn,
          .messaging-page-embedded .messaging-embedded-toolbar .form-check-label,
          .messaging-page-embedded .messaging-embedded-toolbar .text-muted {
            font-size: 12px;
          }

          .messaging-page-embedded .messaging-inbox-shell {
            display: grid !important;
            grid-template-columns: 54px minmax(0, 1fr);
            grid-template-rows: minmax(135px, 30%) minmax(0, 1fr);
          }

          .messaging-page-embedded .messaging-channel-rail {
            grid-column: 1;
            grid-row: 1 / span 2;
            width: 54px !important;
            flex-basis: auto !important;
          }

          .messaging-page-embedded .messaging-conversation-list {
            grid-column: 2;
            grid-row: 1;
            width: auto !important;
            flex-basis: auto !important;
          }

          .messaging-page-embedded .messaging-chat-panel {
            grid-column: 2;
            grid-row: 2;
          }

          .messaging-page-embedded .messaging-chat-panel .card-header {
            padding: 8px 10px;
          }

          .messaging-page-embedded .messaging-chat-panel h5 {
            font-size: 15px;
          }

          .messaging-page-embedded .messaging-composer {
            padding: 8px;
          }

          .messaging-page-embedded .messaging-composer form {
            gap: 6px !important;
          }

          .messaging-page-embedded .messaging-composer .btn,
          .messaging-page-embedded .messaging-composer label.btn {
            flex-basis: 40px !important;
            height: 40px !important;
            width: 40px !important;
          }

          .messaging-page-embedded .messaging-composer input.form-control {
            min-height: 40px !important;
          }
        }
      `}
    </style>
  ) : null;

  return (
    <div className={`messaging-page ${embedded ? 'messaging-page-embedded' : ''}`} style={embedded ? { flex: '1 1 auto', minHeight: 0, width: '100%' } : undefined}>
      {embeddedStyles}
      <div className={`d-flex flex-wrap justify-content-end align-items-center gap-3 mb-2 ${embedded ? 'messaging-embedded-toolbar flex-shrink-0' : ''}`}>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="messagingAutoRefresh"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="messagingAutoRefresh">Auto-sync 15s</label>
        </div>
        <span className="text-muted small">
          {lastSyncAt ? formatDate(lastSyncAt) : 'En attente'}
        </span>
        <div className="d-flex gap-2 ms-auto">
          <button className="btn btn-success" type="button" onClick={runRefresh} disabled={syncing}>
            {syncing ? 'Refresh...' : 'Run refresh'}
          </button>
          {isAdmin && <button className="btn btn-outline-secondary" type="button" onClick={createTestMessage}>Message test</button>}
          {isAdmin && <button className="btn btn-primary" type="button" onClick={() => setShowConfig(true)}>Configurer un canal</button>}
        </div>
      </div>

      {notice && (
        <div className="alert alert-info d-flex justify-content-between align-items-center py-2 mb-2">
          <span>{notice}</span>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setNotice('')} />
        </div>
      )}

      <div
        className="d-flex gap-3 align-items-stretch messaging-inbox-shell"
        style={{
          height: embedded ? 'auto' : 'calc(100vh - 150px)',
          minHeight: embedded ? 0 : 520
        }}
      >
        <aside className="bg-white shadow-sm rounded-4 p-2 d-flex flex-column align-items-center gap-2 messaging-channel-rail" style={{ width: 72, flex: '0 0 72px', minHeight: 0, overflowY: 'auto' }}>
          {filteredChannelTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`border-0 rounded-4 position-relative ${activeType === type.value ? 'bg-dark' : 'bg-transparent'}`}
              title={`${type.label} - ${type.count} canal(aux)`}
              aria-label={`${type.label} - ${type.count} canal(aux)`}
              onClick={() => setActiveType(type.value)}
              style={{ width: 54, height: 54 }}
            >
              <ChannelIcon type={type.value} active={activeType === type.value} size={38} />
              {type.count > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 10 }}>
                  {type.count > 9 ? '9+' : type.count}
                </span>
              )}
            </button>
          ))}
        </aside>

        <section className="card border-0 shadow-sm messaging-conversation-list" style={{ width: 380, flex: '0 0 380px', minHeight: 0 }}>
          <div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="d-flex gap-2 mb-3">
              <input className="form-control" placeholder="Rechercher client, telephone..." value={search} onChange={(e) => setSearch(e.target.value)} />
              <button className="btn btn-outline-primary" type="button" onClick={loadConversations}>OK</button>
            </div>
            <div className="d-flex flex-column gap-2 flex-grow-1" style={{ minHeight: 0, overflowY: 'auto' }}>
              {loading && <div className="text-muted">Chargement...</div>}
              {!loading && conversations.length === 0 && <div className="text-muted">Aucune conversation.</div>}
              {conversations.map((conversation) => (
                <button
                  key={conversation._id}
                  type="button"
                  className={`border rounded-4 p-3 text-start bg-white ${selectedConversation?._id === conversation._id ? 'border-primary shadow-sm' : 'border-light'}`}
                  onClick={() => selectConversation(conversation)}
                >
                  <div className="d-flex justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2 text-truncate">
                      <ChannelIcon type={conversation.channel_type} size={30} />
                      <strong className="text-truncate">{getConversationName(conversation)}</strong>
                    </div>
                    <span className="badge bg-light text-dark d-inline-flex align-items-center gap-1" title={channelLabel(conversation.channel_type)}>
                      <ChannelIcon type={conversation.channel_type} size={18} />
                    </span>
                  </div>
                  <div className="text-muted small text-truncate">{conversation.last_message_preview || 'Sans message'}</div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      <span className="text-muted small">{formatDate(conversation.last_message_at)}</span>
                      {conversation.channel_type === 'site' && getSiteCartCount(conversation) > 0 && (
                        <span className="badge bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1" title="Panier">
                          <i className="fa-solid fa-cart-shopping" />
                          {getSiteCartCount(conversation)}
                        </span>
                      )}
                      {conversation.channel_type === 'site' && getSiteOrdersCount(conversation) > 0 && (
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle d-inline-flex align-items-center gap-1" title="Commandes">
                          <i className="fa-solid fa-receipt" />
                          {getSiteOrdersCount(conversation)}
                        </span>
                      )}
                    </div>
                    {conversation.unread_count > 0 && <span className="badge bg-danger">{conversation.unread_count}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="card border-0 shadow-sm flex-grow-1 d-flex flex-column messaging-chat-panel" style={{ minHeight: 0 }}>
          <div className="card-header bg-white border-0 py-2">
            {selectedConversation ? (
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <ChannelIcon type={selectedConversation.channel_type} size={30} />
                    {getConversationName(selectedConversation)}
                  </h5>
                  <span className="text-muted small">{channelLabel(selectedConversation.channel_type)} - {selectedConversation.contact?.phone || selectedConversation.contact?.username || selectedConversation.contact?.external_id}</span>
                  {selectedConversation.channel_type === 'site' && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                      <span
                        className={`rounded-circle d-inline-flex align-items-center justify-content-center position-relative ${isSiteConversationOnline(selectedConversation) ? 'bg-success text-white' : 'bg-secondary text-white'}`}
                        title={isSiteConversationOnline(selectedConversation) ? 'En ligne' : 'Hors ligne'}
                        aria-label={isSiteConversationOnline(selectedConversation) ? 'En ligne' : 'Hors ligne'}
                        style={{ width: 34, height: 34, flex: '0 0 34px' }}
                      >
                        <i className="fa-solid fa-signal" />
                      </span>
                      <IconCircleButton
                        icon="fa-solid fa-circle-info"
                        title="Infos client"
                        variant="secondary"
                        onClick={() => setShowCustomerContext(true)}
                      />
                      <IconCircleButton
                        icon="fa-solid fa-cart-shopping"
                        title="Panier"
                        variant="success"
                        count={getSiteCartCount(selectedConversation)}
                        onClick={() => setShowCustomerContext(true)}
                      />
                      <IconCircleButton
                        icon="fa-solid fa-receipt"
                        title="Commandes"
                        variant="primary"
                        count={getSiteOrdersCount(selectedConversation)}
                        onClick={() => setShowCustomerContext(true)}
                      />
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div style={{ minWidth: 220 }}>
                    <label className="form-label small text-muted mb-1">Affecter au support</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedConversation.assigned_to?._id || selectedConversation.assigned_to || ''}
                      onChange={(event) => assignConversation(event.target.value)}
                    >
                      <option value="">Non affecte</option>
                      {supportUsers.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name || user.username || user._id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : (
              <h5 className="mb-0">Selectionnez une conversation</h5>
            )}
          </div>
          <div ref={messagesContainerRef} className="card-body bg-light flex-grow-1" style={{ minHeight: 0, overflowY: 'auto' }}>
            {!selectedConversation && <div className="text-muted">Les messages apparaitront ici.</div>}
            {selectedConversation && messagesLoading && (
              <div className="d-flex h-100 align-items-center justify-content-center text-muted fw-semibold">
                Please wait...
              </div>
            )}
            {!messagesLoading && messages.map((message) => (
              <div key={message._id} className={`d-flex mb-3 ${message.direction === 'outbound' ? 'justify-content-end' : 'justify-content-start'}`}>
                <div className={`d-flex align-items-start gap-2 ${message.direction === 'outbound' ? 'flex-row-reverse' : ''}`} style={{ maxWidth: '72%' }}>
                  <div className={`rounded-4 px-3 py-2 shadow-sm ${message.direction === 'outbound' ? 'bg-primary text-white' : 'bg-white'}`} style={{ minWidth: 0 }}>
                    {message.message_type === 'product_card' ? (() => {
                      const product = message.metadata?.product || {};
                      const productImage = message.media?.url || product.image_url || '';
                      const productTitle = product.title || message.text || 'Produit';
                      return (
                        <div className={message.direction === 'outbound' ? 'text-white' : 'text-dark'} style={{ width: 260, maxWidth: '100%' }}>
                          {productImage && (
                            <img
                              src={productImage}
                              alt={productTitle}
                              className="rounded-3 d-block mb-2"
                              onLoad={scrollMessagesToBottom}
                              style={{ width: '100%', height: 180, objectFit: 'cover' }}
                            />
                          )}
                          <h3 className="h5 fw-bold mb-1">{productTitle}</h3>
                          {product.reference && <div className={message.direction === 'outbound' ? 'text-white-50 small' : 'text-muted small'}>Ref: {product.reference}</div>}
                          {product.price && <div className="fw-bold fs-5 mt-1">{product.price} MAD</div>}
                        </div>
                      );
                    })() : message.media?.url ? (
                      <>
                        {(message.message_type === 'image' || String(message.media?.mime_type || '').startsWith('image/')) && (
                          <a href={message.media.url} target="_blank" rel="noreferrer">
                            <img
                              src={message.media.url}
                              alt={message.media?.filename || 'Image message'}
                              className="rounded-3 d-block"
                              onLoad={scrollMessagesToBottom}
                              style={{ maxWidth: 260, maxHeight: 260, objectFit: 'cover' }}
                            />
                          </a>
                        )}
                        {(message.message_type === 'audio' || String(message.media?.mime_type || '').startsWith('audio/')) && (
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <i className="fa-solid fa-file-audio" />
                              <span>{message.media?.filename || 'Audio'}</span>
                            </div>
                            <audio controls src={message.media.url} style={{ width: 260, maxWidth: '100%' }}>
                              Audio non lisible.
                            </audio>
                          </div>
                        )}
                        {(message.message_type === 'video' || String(message.media?.mime_type || '').startsWith('video/')) && (
                          <div>
                            <video controls src={message.media.url} className="rounded-3 d-block" style={{ maxWidth: 280, maxHeight: 260 }}>
                              Video non lisible.
                            </video>
                          </div>
                        )}
                        {!['image', 'audio', 'video'].includes(String(message.message_type || '').toLowerCase()) &&
                          !String(message.media?.mime_type || '').startsWith('image/') &&
                          !String(message.media?.mime_type || '').startsWith('audio/') &&
                          !String(message.media?.mime_type || '').startsWith('video/') && (
                            <a
                              href={message.media.url}
                              target="_blank"
                              rel="noreferrer"
                              className={`d-flex align-items-center gap-2 text-decoration-none ${message.direction === 'outbound' ? 'text-white' : 'text-dark'}`}
                            >
                              <i className={`fa-solid ${getMediaIcon(message)}`} />
                              <span>{message.media?.filename || getMediaTypeLabel(message)}</span>
                            </a>
                          )}
                        {message.text && <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>}
                      </>
                    ) : (
                      <div className="d-flex align-items-center gap-2" style={{ whiteSpace: 'pre-wrap' }}>
                        {message.message_type && message.message_type !== 'text' && <i className={`fa-solid ${getMediaIcon(message)}`} />}
                        <span>{message.text || getMediaTypeLabel(message)}</span>
                      </div>
                    )}
                    <div className={`small mt-1 ${message.direction === 'outbound' ? 'text-white-50' : 'text-muted'}`}>
                      {formatDate(message.createdAt)} - {getMessageStatusLabel(message)}
                    </div>
                    {getMessageErrorHint(message) && (
                      <div className={`small mt-2 rounded-3 px-2 py-1 ${message.direction === 'outbound' ? 'bg-white text-danger' : 'bg-danger-subtle text-danger'}`}>
                        {getMessageErrorHint(message)}
                      </div>
                    )}
                  </div>
                  {isRetryableMessage(message) && (
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0 mt-1"
                      title="Retry"
                      aria-label="Retry failed message"
                      onClick={() => retryMessage(message)}
                      disabled={retryingMessageId === message._id}
                      style={{ lineHeight: 1 }}
                    >
                      <i className={`fa-solid ${retryingMessageId === message._id ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="card-footer bg-white border-0 flex-shrink-0 messaging-composer">
            {isInstagramReplyWindowExpired && (
              <div className="alert alert-warning py-2 mb-2 small">
                Fenetre Instagram expiree: repondez depuis Instagram mobile/web, ou attendez que le client envoie un nouveau message.
              </div>
            )}
            <form className="d-flex align-items-center gap-2" onSubmit={sendMessage}>
              <input
                id="messagingImageUpload"
                type="file"
                accept="image/*"
                multiple
                className="d-none"
                onChange={sendImages}
                disabled={messageInputDisabled || sendingImages}
              />
              <label
                className={`btn d-inline-flex align-items-center justify-content-center shadow-sm mb-0 ${messageInputDisabled || sendingImages ? 'btn-light disabled' : 'btn-outline-secondary'}`}
                htmlFor="messagingImageUpload"
                title="Envoyer des images"
                aria-label="Envoyer des images"
                style={{
                  borderRadius: '50%',
                  flex: '0 0 48px',
                  height: 48,
                  width: 48
                }}
              >
                <i className={`fa-solid ${sendingImages ? 'fa-spinner fa-spin' : 'fa-image'}`} />
              </label>
              <button
                type="button"
                className="btn btn-outline-success d-inline-flex align-items-center justify-content-center shadow-sm"
                disabled={messageInputDisabled || sendingProducts}
                title={productsLoading ? 'Chargement des produits...' : 'Envoyer des produits'}
                aria-label="Envoyer des produits"
                onClick={() => setShowProductsModal(true)}
                style={{
                  borderRadius: '50%',
                  flex: '0 0 48px',
                  height: 48,
                  width: 48
                }}
              >
                <i className={`fa-solid ${sendingProducts || productsLoading ? 'fa-spinner fa-spin' : 'fa-box-open'}`} />
              </button>
              <input
                className="form-control border-0 shadow-sm"
                disabled={messageInputDisabled || sendingImages || sendingProducts}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={isInstagramReplyWindowExpired ? 'Fenetre Instagram expiree' : 'Ecrire un message...'}
                style={{
                  borderRadius: 999,
                  minHeight: 48,
                  paddingLeft: 20,
                  paddingRight: 20
                }}
              />
              <button
                className="btn btn-primary d-inline-flex align-items-center justify-content-center shadow-sm"
                disabled={messageInputDisabled || sendingImages || !text.trim()}
                type="submit"
                title="Envoyer"
                aria-label="Envoyer"
                style={{
                  borderRadius: '50%',
                  flex: '0 0 48px',
                  height: 48,
                  width: 48
                }}
              >
                <i className="fa-solid fa-paper-plane" />
              </button>
            </form>
          </div>
        </section>
      </div>

      {showProductsModal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Envoyer des produits</h5>
                  <div className="text-muted small">
                    {productsLoading ? 'Chargement du catalogue...' : `${products.length} produit(s) charges - ${selectedProductIds.length} selectionne(s)`}
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowProductsModal(false)} />
              </div>
              <div
                className="modal-body"
                onScroll={(event) => {
                  const target = event.currentTarget;
                  if (target.scrollTop + target.clientHeight >= target.scrollHeight - 180) {
                    setVisibleProductLimit((prev) => Math.min(prev + 60, filteredProducts.length));
                  }
                }}
              >
                <div className="row g-2 mb-3">
                  <div className="col-md-5">
                    <input
                      className="form-control"
                      placeholder="Rechercher produit, ref, code-barres..."
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      placeholder="Prix min"
                      value={productPriceMin}
                      onChange={(event) => setProductPriceMin(event.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      placeholder="Prix max"
                      value={productPriceMax}
                      onChange={(event) => setProductPriceMax(event.target.value)}
                    />
                  </div>
                  <div className="col-md-3 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => {
                        setProductSearch('');
                        setProductPriceMin('');
                        setProductPriceMax('');
                        setSelectedProductCategory('');
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={!selectedProductIds.length}
                      onClick={() => {
                        setSelectedProductIds([]);
                        setProductPriceOverrides({});
                      }}
                    >
                      Vider
                    </button>
                  </div>
                </div>

                <div className="d-flex gap-3 overflow-auto pb-3 mb-3" style={{ scrollSnapType: 'x mandatory' }}>
                  <button
                    type="button"
                    className={`border rounded-4 p-2 bg-white flex-shrink-0 ${selectedProductCategory === '' ? 'border-success shadow-sm' : 'border-light'}`}
                    onClick={() => setSelectedProductCategory('')}
                    style={{ width: 116, scrollSnapAlign: 'start' }}
                  >
                    <div className="mx-auto rounded-circle d-flex align-items-center justify-content-center bg-light" style={{ width: 74, height: 74 }}>
                      <i className="fa-solid fa-boxes-stacked text-success fs-4" />
                    </div>
                    <div className="small fw-bold text-truncate mt-2">Tous</div>
                  </button>
                  {pegasusCategories.map((category) => {
                    const categoryId = String(category._id);
                    const imageUrl = resolveCategoryImageUrl(category);
                    return (
                      <button
                        key={categoryId}
                        type="button"
                        className={`border rounded-4 p-2 bg-white flex-shrink-0 ${selectedProductCategory === categoryId ? 'border-success shadow-sm' : 'border-light'}`}
                        onClick={() => setSelectedProductCategory(categoryId)}
                        style={{ width: 116, scrollSnapAlign: 'start' }}
                        title={category.name || category.label || categoryId}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={category.name || 'Categorie'}
                            className="rounded-circle d-block mx-auto"
                            style={{ width: 74, height: 74, objectFit: 'cover', background: '#f3f4f6' }}
                          />
                        ) : (
                          <div className="mx-auto rounded-circle bg-light" style={{ width: 74, height: 74 }} />
                        )}
                        <div className="small fw-bold text-truncate mt-2">{category.name || category.label || 'Categorie'}</div>
                      </button>
                    );
                  })}
                </div>

                {productsLoading && <div className="text-muted">Chargement des produits...</div>}
                {!productsLoading && filteredProducts.length === 0 && <div className="text-muted">Aucun produit trouve.</div>}
                <div className="row g-3">
                  {visibleProducts.map((product) => {
                    const productId = String(product._id || product.id || '');
                    const selected = selectedProductIds.includes(productId);
                    const imageUrl = resolveProductImageUrl(product);
                    const productPrice = getEffectiveProductPrice(product);
                    return (
                      <div className="col-md-4 col-xl-3" key={productId}>
                        <div
                          role="button"
                          tabIndex={0}
                          className={`card h-100 text-start w-100 border ${selected ? 'border-success shadow' : 'border-light shadow-sm'}`}
                          onClick={() => toggleProductSelection(productId)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleProductSelection(productId);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="position-relative">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={product.name || 'Produit'}
                                className="card-img-top"
                                style={{ height: 150, objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: 150 }}>
                                <i className="fa-solid fa-image text-muted fs-3" />
                              </div>
                            )}
                            {selected && <span className="badge bg-success position-absolute top-0 end-0 m-2">Selectionne</span>}
                          </div>
                          <div className="card-body">
                            <strong className="d-block text-truncate">{product.name || 'Produit sans nom'}</strong>
                            <div className="small text-muted text-truncate">Ref: {getProductReference(product) || '-'}</div>
                            <div className="d-flex align-items-center justify-content-between gap-2 mt-1">
                              <div className="fw-bold text-success">{productPrice || '-'} MAD</div>
                              {selected && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success rounded-circle d-inline-flex align-items-center justify-content-center"
                                  title="Modifier le prix a envoyer"
                                  aria-label="Modifier le prix a envoyer"
                                  onClick={(event) => editSelectedProductPrice(event, product)}
                                  style={{ width: 32, height: 32, flex: '0 0 32px' }}
                                >
                                  <i className="fa-solid fa-pen" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!productsLoading && visibleProducts.length < filteredProducts.length && (
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      onClick={() => setVisibleProductLimit((prev) => Math.min(prev + 60, filteredProducts.length))}
                    >
                      Charger plus ({visibleProducts.length}/{filteredProducts.length})
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowProductsModal(false)}>Fermer</button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={!selectedProductIds.length || sendingProducts || messageInputDisabled}
                  onClick={sendSelectedProducts}
                >
                  {sendingProducts ? 'Envoi...' : `Envoyer ${selectedProductIds.length} produit(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCustomerContext && selectedConversation && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Infos client site</h5>
                  <div className="text-muted small">{getConversationName(selectedConversation)}</div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowCustomerContext(false)} />
              </div>
              <div className="modal-body">
                {(() => {
                  const context = getSiteCustomerContext(selectedConversation);
                  const contact = selectedConversation.contact || {};
                  const cart = context.cart || {};
                  const cartItems = Array.isArray(cart.items) ? cart.items : [];
                  const orders = context.orders || {};
                  const orderItems = Array.isArray(orders.items) ? orders.items : [];
                  const presence = getSitePresence(selectedConversation);
                  const device = presence.device || {};
                  const visitedPages = Array.isArray(presence.visited_pages) ? presence.visited_pages : [];
                  const sessions = getSitePresenceSessions(selectedConversation);
                  const online = isSiteConversationOnline(selectedConversation);
                  return (
                    <>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <div className="border rounded-4 p-3 h-100 bg-light">
                            <h6 className="d-flex align-items-center gap-2 mb-3">
                              <i className="fa-solid fa-circle-info text-secondary" />
                              Infos client
                            </h6>
                            <div><strong>Nom:</strong> {contact.name || '-'}</div>
                            <div><strong>Telephone:</strong> {contact.phone || '-'}</div>
                            <div><strong>Email:</strong> {contact.email || '-'}</div>
                            <div><strong>Identifiant:</strong> {contact.external_id || '-'}</div>
                            <div><strong>Connecte:</strong> {context.logged_in ? 'Oui' : 'Non'}</div>
                            <div className="text-break"><strong>Page:</strong> {selectedConversation.metadata?.last_page_url || '-'}</div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="border rounded-4 p-3 h-100 bg-success-subtle">
                            <h6 className="d-flex align-items-center gap-2">
                              <i className="fa-solid fa-cart-shopping text-success" />
                              Panier
                            </h6>
                            <div className="fs-3 fw-bold">{getSiteCartCount(selectedConversation)}</div>
                            <div className="text-muted small">{formatSiteMoney(cart.total_text || cart.total_amount || cart.total, cart.currency)}</div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="border rounded-4 p-3 h-100 bg-primary-subtle">
                            <h6 className="d-flex align-items-center gap-2">
                              <i className="fa-solid fa-receipt text-primary" />
                              Commandes
                            </h6>
                            <div className="fs-3 fw-bold">{getSiteOrdersCount(selectedConversation)}</div>
                            <div className="text-muted small">Dernieres commandes</div>
                          </div>
                        </div>
                      </div>

                      <h6 className="mt-4 d-flex align-items-center gap-2">
                        <i className="fa-solid fa-signal text-success" />
                        Presence et navigation
                      </h6>
                      <div className="border rounded-4 p-3 bg-light mb-3">
                        <div className="row g-3">
                          <div className="col-md-4">
                            <div><strong>Statut:</strong> <span className={`badge ${online ? 'bg-success' : 'bg-secondary'}`}>{online ? 'En ligne' : 'Hors ligne'}</span></div>
                            <div><strong>Derniere activite:</strong> {formatDate(presence.last_seen_at) || '-'}</div>
                            <div className="text-break"><strong>Session:</strong> {presence.session_id || '-'}</div>
                            <div className="text-break">
                              <strong>Page courante:</strong>{' '}
                              {presence.current_page ? <a href={presence.current_page} target="_blank" rel="noreferrer">{presence.current_page}</a> : '-'}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div><strong>Plateforme:</strong> {device.platform || '-'}</div>
                            <div><strong>Langue:</strong> {device.language || '-'}</div>
                            <div><strong>Ecran:</strong> {device.screen || '-'}</div>
                            <div><strong>Viewport:</strong> {device.viewport || '-'}</div>
                            <div><strong>Timezone:</strong> {device.timezone || '-'}</div>
                            <div><strong>Cookies:</strong> {device.cookie_enabled ? 'Oui' : 'Non'}</div>
                          </div>
                          <div className="col-md-4">
                            <strong>User agent:</strong>
                            <div className="small text-muted text-break">{device.user_agent || '-'}</div>
                          </div>
                        </div>
                        <div className="mt-3">
                          <strong>Pages visitees:</strong>
                          {visitedPages.length === 0 ? (
                            <div className="text-muted small">Aucune page disponible.</div>
                          ) : (
                            <div className="d-flex flex-column gap-1 mt-1">
                              {visitedPages.map((page, index) => (
                                <a key={`${page}-${index}`} className="small text-break" href={page} target="_blank" rel="noreferrer">{page}</a>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="mt-3">
                          <strong>Sessions:</strong>
                          {sessions.length === 0 ? (
                            <div className="text-muted small">Aucune session disponible.</div>
                          ) : (
                            <div className="table-responsive mt-2">
                              <table className="table table-sm align-middle mb-0">
                                <thead>
                                  <tr>
                                    <th>Statut</th>
                                    <th>Derniere activite</th>
                                    <th>Page</th>
                                    <th>Device</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sessions.map((session, index) => {
                                    const sessionDevice = session.device || {};
                                    const sessionOnline = (() => {
                                      const lastSeen = new Date(session.last_seen_at).getTime();
                                      return Number.isFinite(lastSeen) && Date.now() - lastSeen < 90000;
                                    })();
                                    return (
                                      <tr key={`${session.session_id || index}-${index}`}>
                                        <td><span className={`badge ${sessionOnline ? 'bg-success' : 'bg-secondary'}`}>{sessionOnline ? 'Online' : 'Offline'}</span></td>
                                        <td>{formatDate(session.last_seen_at) || '-'}</td>
                                        <td className="text-break">{session.current_page ? <a href={session.current_page} target="_blank" rel="noreferrer">{session.current_page}</a> : '-'}</td>
                                        <td className="text-break small">
                                          {sessionDevice.platform || '-'} / {sessionDevice.viewport || '-'} / {sessionDevice.timezone || '-'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>

                      <h6 className="mt-4">Panier courant</h6>
                      {cartItems.length === 0 ? (
                        <div className="text-muted">Panier vide ou non disponible.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th>Produit</th>
                                <th>SKU</th>
                                <th>Qte</th>
                                <th>Prix</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cartItems.map((item, index) => (
                                <tr key={`${item.product_id || index}-${index}`}>
                                  <td>
                                    {item.permalink ? <a href={item.permalink} target="_blank" rel="noreferrer">{item.name || '-'}</a> : (item.name || '-')}
                                  </td>
                                  <td>{item.sku || '-'}</td>
                                  <td>{item.quantity || 0}</td>
                                  <td>{formatSiteMoney(item.price || 0, cart.currency)}</td>
                                  <td>{formatSiteMoney(item.line_total || 0, cart.currency)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <h6 className="mt-4">Anciennes commandes</h6>
                      {orderItems.length === 0 ? (
                        <div className="text-muted">Aucune commande disponible.</div>
                      ) : (
                        <div className="accordion" id="siteCustomerOrders">
                          {orderItems.map((order, index) => (
                            <div className="accordion-item" key={`${order.id || order.number || index}`}>
                              <h2 className="accordion-header">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#site-order-${index}`}>
                                  #{order.number || order.id} - {order.status || '-'} - {order.total || 0} {order.currency || ''}
                                </button>
                              </h2>
                              <div id={`site-order-${index}`} className="accordion-collapse collapse" data-bs-parent="#siteCustomerOrders">
                                <div className="accordion-body">
                                  <div className="row g-2 mb-2 small">
                                    <div className="col-md-3"><strong>Date:</strong> {order.date || '-'}</div>
                                    <div className="col-md-3"><strong>Paiement:</strong> {order.payment_method || '-'}</div>
                                    <div className="col-md-3"><strong>Telephone:</strong> {order.billing_phone || '-'}</div>
                                    <div className="col-md-3"><strong>Email:</strong> {order.billing_email || '-'}</div>
                                  </div>
                                  <ul className="mb-0">
                                    {(Array.isArray(order.items) ? order.items : []).map((item, itemIndex) => (
                                      <li key={`${item.product_id || itemIndex}-${itemIndex}`}>
                                        {item.quantity || 0} x {item.name || '-'} - {item.total || 0}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfig && isAdmin && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <form className="modal-content" onSubmit={saveChannel}>
              <div className="modal-header">
                <h5 className="modal-title">Configuration canal</h5>
                <button type="button" className="btn-close" onClick={() => setShowConfig(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Nom</label>
                    <input className="form-control" value={channelForm.name} onChange={(e) => setChannelForm((prev) => ({ ...prev, name: e.target.value }))} required />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Canal</label>
                    <select className="form-select" value={channelForm.type} onChange={(e) => setChannelForm((prev) => ({ ...prev, type: e.target.value }))}>
                      {CHANNEL_TYPES.filter((item) => item.value !== 'all').map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Statut</label>
                    <select className="form-select" value={channelForm.enabled ? '1' : '0'} onChange={(e) => setChannelForm((prev) => ({ ...prev, enabled: e.target.value === '1' }))}>
                      <option value="1">Actif</option>
                      <option value="0">Inactif</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">ID compte externe</label>
                    <input className="form-control" value={channelForm.external_account_id} onChange={(e) => setChannelForm((prev) => ({ ...prev, external_account_id: e.target.value }))} placeholder="WABA ID, Instagram App ID, fournisseur..." />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Sender ID</label>
                    <input className="form-control" value={channelForm.external_sender_id} onChange={(e) => setChannelForm((prev) => ({ ...prev, external_sender_id: e.target.value }))} placeholder="Phone Number ID ou Instagram Business/Page ID" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Access token</label>
                    <input type="password" className="form-control" value={channelForm.credentials?.access_token || ''} onChange={(e) => setChannelForm((prev) => ({ ...prev, credentials: { ...prev.credentials, access_token: e.target.value } }))} placeholder="Meta permanent token / token fournisseur" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Verify token webhook</label>
                    <input className="form-control" value={channelForm.webhook?.verify_token || ''} onChange={(e) => setChannelForm((prev) => ({ ...prev, webhook: { ...prev.webhook, verify_token: e.target.value }, credentials: { ...prev.credentials, verify_token: e.target.value } }))} />
                  </div>
                  <div className="col-12">
                    <div className="alert alert-secondary mb-0">
                      Callback URL a utiliser dans Meta: <code>{getWebhookUrl(channelForm.type)}</code>
                    </div>
                  </div>
                </div>

                {channels.length > 0 && (
                  <div className="mt-4">
                    <h6>Canaux existants</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {channels.map((channel) => (
                        <button key={channel._id} type="button" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2" onClick={() => editChannel(channel)}>
                          <ChannelIcon type={channel.type} size={22} />
                          {channel.name} - {channelLabel(channel.type)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowConfig(false)}>Fermer</button>
                <button type="submit" className="btn btn-primary">Sauvegarder</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagingInbox;
